const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  addToCartTextShort: '[data-product-add-to-cart-text-short]',
  media: '[data-product-media]',
  price: '[data-product-price]',
  differentGraphicMessage: '[data-product-different-graphic-message]',
  purchaseLimitMessage: '[data-purchase-limit-message]',
  backInStock: '[data-product-bis]',
  forms: '[data-product-forms]',
}

export class ProductInfo extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  abortController = undefined
  pendingRequestUrl = undefined
  productIsAvailable = undefined
  preReplaceProductOptionsCallbacks = []
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()
  }

  setupListeners () {
    super.setupListeners()

    this.productIsAvailable = !window.BAO.Utils.getElement(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )?.hasAttribute('disabled')

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    this.postReplacePageCallbacks.push(() => {
      window.yotpoWidgetsContainer?.initWidgets()
    })

    if (this.hasAttribute('data-regios-manual-price')) {
      this.updateRegiosPrice()

      this.onExternalOptionChange = this.onExternalOptionChange.bind(this)
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
        this.onExternalOptionChange,
      )
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    if (this.hasAttribute('data-regios-manual-price')) {
      window.BAO.eventBus.removeEventListener(
        window.BAO.EVENTS.PRODUCT.OPTION_CHANGED,
        this.onExternalOptionChange,
      )
    }
  }

  onExternalOptionChange ({ detail: { sectionId, productId, variant } }) {
    if (sectionId === this.sectionId) return
    if (!variant) return
    if (Number(productId) !== Number(this.dataset.productId)) return

    this.updateRegiosPrice(variant)
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {string[]} payload.detail.selectedOptionValues
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId

      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {HTMLOptionElement | EventTarget} payload.detail.target
   * @param {string[]} payload.detail.selectedOptionValues
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    if (!this.contains(target)) return
    if (this.dataset.productInfoId !== target.dataset.productInfoId) return

    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })

    const productUpsells = window.BAO.Utils.getElement('product-upsells')

    productUpsells &&
      productUpsells.filterRenderedProducts(
        target.value,
        target.dataset.variantAvailable === 'true',
      )
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   */

  /**
   * @param {Object} payload
   * @param {string} payload.requestUrl
   * @param {string} payload.targetId
   * @param {fetchProductDetailsCallback} payload.callback
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const text = await fetchProductData(
        requestUrl,
        this.abortController.signal,
      )

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      window.BAO.Utils.getElement(`#${targetId}`)?.focus()
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)
        this.loading = false
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   */
  handleSwapProduct (productUrl, updateFullPage = false) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      this.updateURL(productUrl, variant?.id)

      if (variant) {
        window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
          sectionId: this.sectionId,
          productId: this.dataset.productId,
          html,
          variant,
        })
      }

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        window.BAO.Utils.gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      } else {
        window.BAO.Utils.gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      }
    }
  }

  /**
   * @param {string} productUrl
   */
  renderProductDetails (productUrl) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (!variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)

        this.loading = false

        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id)
      this.updateVariantInputs(variant?.id)

      /**
       * @callback PostReplaceCallback
       * @param {Element} currentElement
       * @param {Element} newElement
       */

      /**
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = window.BAO.Utils.getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
        () => {
          if (
            window.BAO.MediaBreakpoints.isMd &&
            this.dataset.updateUrl === 'true'
          ) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        },
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.forms}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.differentGraphicMessage}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.purchaseLimitMessage}`,
      )

      const productIsAvailable = !html
        .querySelector('product-form button[type=submit]')
        ?.hasAttribute('disabled')

      this.updateAvailability(productIsAvailable)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        productId: this.dataset.productId,
        html,
        variant,
      })

      if (this.hasAttribute('data-regios-manual-price')) {
        this.dataset.variantId = variant.id
        this.updateRegiosPrice(variant)
      }

      this.loading = false
    }
  }

  updateAvailability (productIsAvailable, text) {
    const addToCartTextEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartText}`,
    )
    const addToCartTextShortEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartTextShort}`,
    )
    const backInStockEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStock}`,
    )

    this.productIsAvailable = productIsAvailable
    this.syncAddToCartButtons()

    backInStockEls.forEach((backInStockEl) => {
      backInStockEl.setAttribute('aria-hidden', productIsAvailable)
    })

    addToCartTextEls.forEach((addToCartTextEl) => {
      addToCartTextEl.textContent =
        text || productIsAvailable
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut
    })

    addToCartTextShortEls.forEach((addToCartShortTextEl) => {
      addToCartShortTextEl.textContent =
        text || productIsAvailable
          ? window.theme.strings.addToCartShort
          : window.theme.strings.soldOut
    })
  }

  syncAddToCartButtons () {
    const addToCartEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )

    addToCartEls.forEach((addToCartEl) => {
      addToCartEl.toggleAttribute(
        'disabled',
        this.loading || !this.productIsAvailable,
      )
    })
  }

  /**
   * @param {Document} html
   */
  updateOptionValues (html) {
    const productOptions = html.querySelector(
      `${this.productOptions.tagName.toLowerCase()}`,
    )

    if (productOptions) {
      window.BAO.Utils.gracefullyReplaceNode(
        this.productOptions,
        productOptions,
        this.preReplaceProductOptionsCallbacks,
      )
    }
  }

  /**
   * @param {string} url
   * @param {string} [variantId]
   */
  updateURL (url, variantId) {
    if (this.dataset.updateUrl === 'false') return

    const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`

    window.history.replaceState({}, '', newUrl)

    const shareButtons = window.BAO.Utils.getElements('share-button')

    if (!shareButtons) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(newUrl)
    })
  }

  /**
   * @param {string} [variantId]
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    if (this.els.form?.element) {
      const input = this.productForm.querySelector('input[name="id"]')
      if (!input) return

      input.value = variantId
      input.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  /**
   * @param {string} url
   * @param {string[]} optionValues
   * @param {boolean} shouldFetchFullPage
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  async updateRegiosPrice (selectedVariant = null) {
    const dataElement = this.querySelector('[data-regios-product]')

    if (!dataElement) return

    let product

    try {
      product = JSON.parse(dataElement.textContent)
    } catch (error) {
      console.warn('[ProductInfo] Could not parse Regios product data', error)

      return
    }

    if (selectedVariant) {
      const variant = product.variants.find(
        ({ id }) => id === Number(selectedVariant.id),
      )

      if (!variant) return

      product.variantId = variant.id
      product.regularPriceInCents = variant.priceInCents
      product.compareAtPriceInCents = variant.compareAtPriceInCents
    }

    const priceContainer = this.querySelector(
      '.regios-dopp-generic-price-container',
    )
    const salePriceElement = priceContainer?.querySelector(
      '.regios-dopp-generic-price-item--sale',
    )
    const regularPriceElement = priceContainer?.querySelector(
      '.regios-dopp-generic-price-item--regular',
    )

    if (!priceContainer || !salePriceElement || !regularPriceElement) {
      return
    }

    const api = await this.getRegiosApi()

    if (!api) return

    try {
      const { salePrice, regularPrice } =
        await api.calculateDiscountedPrices(product)

      const isDiscounted = salePrice.amount < regularPrice.amount

      this.updateRegiosPriceText(salePriceElement, salePrice.formatted)

      if (isDiscounted) {
        this.updateRegiosPriceText(regularPriceElement, regularPrice.formatted)

        regularPriceElement.style.removeProperty('display')

        priceContainer.classList.add('regios-dopp-generic-price--on-sale')
      } else {
        this.updateRegiosPriceText(salePriceElement, regularPrice.formatted)
        this.updateRegiosPriceText(regularPriceElement, '')

        regularPriceElement.style.display = 'none'

        priceContainer.classList.remove('regios-dopp-generic-price--on-sale')
      }
    } catch (error) {
      console.warn(
        `[ProductInfo] Could not calculate Regios price for product ${product.productId}`,
        error,
      )
    }
  }

  updateRegiosPriceText (element, value) {
    const screenReaderElement = element.querySelector('.util-ScreenReaderOnly')

    element.replaceChildren()

    if (screenReaderElement) {
      element.append(screenReaderElement)
    }

    element.append(document.createTextNode(value))
  }

  getRegiosApi () {
    if (window.RegiosDOPP?.api?.v0) {
      return Promise.resolve(window.RegiosDOPP.api.v0)
    }

    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        resolve(null)
      }, 5000)

      window.addEventListener(
        'regios-dopp:api-initialized',
        (event) => {
          window.clearTimeout(timeout)

          resolve(event.detail.api)
        },
        { once: true },
      )
    })
  }

  /**
   * @param {Document} productInfoNode
   *
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    const selectedVariantDataEl = productInfoNode.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="selected-variant"]`,
    )
    if (!selectedVariantDataEl) return null

    return JSON.parse(selectedVariantDataEl.innerHTML)
  }

  get productForm () {
    return this.els.form?.element
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els.options.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
    this.syncAddToCartButtons()
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * @param {string} requestUrl
 * @param {AbortSignal} abortSignal
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
